import Uppy from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'
import { authFetch } from '@/services/apiClient'

// อัปโหลดไฟล์ตรงจากเบราว์เซอร์ไปที่เก็บไฟล์ (MinIO / AWS S3) ด้วย Uppy (@uppy/aws-s3) — ไฟล์ไม่ผ่าน API จึงไม่ติดขีดจำกัดขนาดของ nginx/gunicorn
// ขั้นตอน: init (ขอสิทธิ์) → ส่งตรงไปที่เก็บ (ไฟล์ใหญ่แบ่งชิ้น ลองใหม่/ทำต่อได้) → finalize (API ตรวจไฟล์ + ย้ายไปปลายทาง + บันทึก DB)
// ฝั่ง API: /api/uploads/* (jos-cowork-api: services/upload_service.py) — ขนาดสูงสุด/ชนิดไฟล์กำหนดที่ server (GET config) · ต้องล็อกอิน

const MB = 1024 * 1024
const DEFAULT_THRESHOLD = 50 * MB   // ต้องตรงกับ MULTIPART_THRESHOLD ของ API (อ่านจาก GET config อีกชั้น)

export type DirectUploadPurpose = 'IMAGE' | 'PROFILE_RESUME' | 'APPLICATION_DOC'

export interface DirectUploadOptions {
  purpose: DirectUploadPurpose
  extra?: Record<string, unknown>          // เช่น { application_id, kind } ของ APPLICATION_DOC
  onProgress?: (percent: number) => void   // 0–100
  signal?: AbortSignal                     // ยกเลิกกลางทางได้
}

/** ใช้ทางอัปโหลดตรงไม่ได้ (API ยังไม่มี endpoint นี้ ฯลฯ) — ผู้เรียกควรถอยไปใช้ทางเดิม (request.files) */
export class DirectUploadUnavailable extends Error {}

interface ApiResult<T> { success: boolean; data?: T; error?: string; message?: string }

async function call<T = any>(path: string, body?: unknown): Promise<T> {
  const res = await authFetch(`/uploads/${path}`, body === undefined
    ? { method: 'GET' }
    : { method: 'POST', body: JSON.stringify(body) })
  const json: ApiResult<T> | null = await res.json().catch(() => null)
  if (res.status === 404 && !json?.success) throw new DirectUploadUnavailable('ไม่พบ endpoint อัปโหลดตรง')
  if (!json?.success) throw new Error(json?.error || json?.message || 'อัปโหลดไม่สำเร็จ')
  return json.data as T
}

let thresholdPromise: Promise<number> | null = null
const getThreshold = () => {
  if (!thresholdPromise) {
    thresholdPromise = call<{ multipart_threshold: number }>('config')
      .then((c) => c.multipart_threshold || DEFAULT_THRESHOLD)
      .catch((e) => {
        thresholdPromise = null   // ลองใหม่ครั้งหน้า
        throw e
      })
  }
  return thresholdPromise
}

// ขนาดชิ้นส่วนของไฟล์ใหญ่: ชิ้นละ ≥ 10 MB และไม่เกิน 9,000 ชิ้น — ส่งให้ API ตอน init เพื่อนับจำนวนชิ้นสูงสุดให้ตรงกัน
const partSizeFor = (size: number) => Math.max(10 * MB, Math.ceil(size / 9000 / MB) * MB)

// endpoint หายระหว่างใช้งาน (เช่น API ถูกย้อนเวอร์ชัน): Uppy ห่อ error ของ callback ใหม่ (class หาย เหลือแต่ข้อความ) จึงดูจากข้อความด้วย
const isUnavailable = (e: any): boolean => e instanceof DirectUploadUnavailable || /ไม่พบ endpoint อัปโหลดตรง/.test(String(e?.message || ''))

const friendly = (e: unknown): string => {
  const msg = String((e as Error)?.message || e || '')
  if (/network|failed to fetch|load failed|xhr|cors|status of 0/i.test(msg)) {
    return 'ส่งไฟล์ไปที่เก็บไฟล์ไม่สำเร็จ (เครือข่ายขัดข้อง) กรุณาลองใหม่อีกครั้ง'
  }
  return msg || 'อัปโหลดไม่สำเร็จ'
}

/** อัปโหลดไฟล์ 1 ไฟล์ตรงไปที่เก็บ แล้วคืนผลของ finalize (รูป: {url,key} · เรซูเม่: โปรไฟล์ · เอกสารใบสมัคร: {documents}) — ล้มเหลว throw Error(ข้อความภาษาไทย) */
export async function uploadDirect<T = any>(file: File, opts: DirectUploadOptions): Promise<T> {
  const threshold = await getThreshold()   // ถ้า API ยังไม่มี endpoint นี้ → DirectUploadUnavailable (ผู้เรียกถอยไปทางเดิม)
  const multipart = file.size > threshold
  const partSize = partSizeFor(file.size)

  let token = ''
  let key = ''
  const initBody = (extra: Record<string, unknown>) => ({
    purpose: opts.purpose, filename: file.name, size: file.size, ...(opts.extra || {}), ...extra,
  })

  const uppy = new Uppy({ autoProceed: false, allowMultipleUploads: false })
  uppy.use(AwsS3, {
    shouldUseMultipart: () => multipart,
    getChunkSize: () => partSize,
    // ไฟล์เล็ก: ส่งก้อนเดียวด้วย POST policy (ที่เก็บบังคับขนาดและชนิดให้)
    getUploadParameters: async () => {
      const init = await call<any>('init', initBody({ multipart: false }))
      token = init.upload_token
      key = init.key
      return { method: 'POST' as const, url: init.url as string, fields: init.fields as Record<string, string> }
    },
    // ไฟล์ใหญ่: multipart — API เป็นผู้ออก URL ของแต่ละชิ้น
    createMultipartUpload: async () => {
      const init = await call<any>('init', initBody({ multipart: true, part_size: partSize }))
      token = init.upload_token
      key = init.key
      return { uploadId: init.upload_id as string, key: init.key as string }
    },
    signPart: async (_file, { partNumber }) => {
      const r = await call<{ url: string }>('multipart/sign-part', { upload_token: token, part_number: partNumber })
      return { url: r.url }
    },
    listParts: async () => (await call<{ parts: any[] }>('multipart/list-parts', { upload_token: token })).parts,
    completeMultipartUpload: async (_file, { parts }) => {
      await call('multipart/complete', { upload_token: token, parts: parts.map((p) => ({ PartNumber: p.PartNumber, ETag: p.ETag })) })
      return { location: key }
    },
    abortMultipartUpload: async () => {
      try { await call('multipart/abort', { upload_token: token }) } catch { /* ล้างไม่สำเร็จก็ปล่อย — lifecycle ของที่เก็บลบให้ */ }
    },
  })

  uppy.on('upload-progress', (_f, p) => {
    if (opts.onProgress && p.bytesTotal) opts.onProgress(Math.min(100, Math.round(((p.bytesUploaded as number) / p.bytesTotal) * 100)))
  })
  const onAbort = () => uppy.cancelAll()
  opts.signal?.addEventListener('abort', onAbort)

  try {
    uppy.addFile({ name: file.name, type: file.type || 'application/octet-stream', data: file, source: 'Local' })
    const result = await uppy.upload()
    if (!result || result.failed?.length || !token) {
      // endpoint หายระหว่างใช้งาน (เช่น API ถูกย้อนเวอร์ชัน): Uppy ห่อ error ของ callback ใหม่ จึงดูจากข้อความด้วย → ให้ผู้เรียกถอยไปทางเดิม
      const err: any = result?.failed?.[0]?.error
      if (isUnavailable(err)) {
        thresholdPromise = null
        throw new DirectUploadUnavailable('ไม่พบ endpoint อัปโหลดตรง')
      }
      throw new Error(friendly(err) || 'อัปโหลดไม่สำเร็จ')
    }
    opts.onProgress?.(100)
    return await call<T>('finalize', { upload_token: token })
  } catch (e) {
    if (isUnavailable(e)) { thresholdPromise = null; throw new DirectUploadUnavailable('ไม่พบ endpoint อัปโหลดตรง') }
    throw new Error(friendly(e))
  } finally {
    opts.signal?.removeEventListener('abort', onAbort)
    uppy.destroy()
  }
}
